import React, { useCallback, useEffect, useState, useRef } from 'react';
import Popup from '@stardust/popup';
import { bodyScroll } from '@shopee/body-scroll';
import { I18n } from '@shopee/platform';

export type ShowPopupTypes = {
  title?: string | null | undefined;
  message?: string | React.ReactNode;
  errorMessage?: string | null | React.ReactNode;
  mainButtonLabel?: string | null | undefined;
  secondaryButtonLabel?: string | null | undefined;
  onClickMainButton?: (() => void | Promise<void>) | null | undefined;
  onClickSecondaryButton?: (() => void) | null | undefined;
  onClickOverlay?: (() => void) | null | undefined;
  onDismiss?: (() => void) | null | undefined;
};

export type ComponentWithPopupTypes = {
  showPopup: (params: ShowPopupTypes) => void;
  hidePopup: () => void;
  popupElement: React.ReactNode;
  unMountPopup: () => void;
};

export function withPopupHigher(wrapperClassName: string) {
  return <P extends ComponentWithPopupTypes>(
    WrappedComponent: React.ComponentType<P>
  ) => {
    function ComponentWithPopup(props: Omit<P, keyof ComponentWithPopupTypes>) {
      const [popupElement, setPopupElement] = useState<
        React.ReactNode | null | undefined
      >(null);
      const onDismissRef = useRef<(() => void) | null | undefined>(null);

      useEffect(() => {
        if (popupElement) {
          bodyScroll.prevent();
          return () => {
            bodyScroll.resume();
          };
        }
      }, [!!popupElement]);

      const showPopup = useCallback(
        ({
          title,
          message,
          mainButtonLabel,
          secondaryButtonLabel,
          onClickMainButton,
          onClickSecondaryButton,
          onClickOverlay,
          onDismiss,
        }: ShowPopupTypes) => {
          onDismissRef.current = onDismiss;
          setPopupElement(
            <Popup
              className={wrapperClassName}
              mainButtonLabel={mainButtonLabel || I18n.t('label_ok')}
              secondaryButtonLabel={secondaryButtonLabel || null}
              onClickMainButton={() => {
                onClickMainButton?.();
                hidePopup();
              }}
              onClickSecondaryButton={() => {
                onClickSecondaryButton?.();
                hidePopup();
              }}
              onClickOverlay={() => {
                onClickOverlay?.();
              }}
              title={title}
            >
              {message}
            </Popup>
          );
        },
        []
      );

      const hidePopup = useCallback(() => {
        onDismissRef.current?.();
        // cleanup
        onDismissRef.current = null;
        setPopupElement(null);
      }, []);

      const unMountPopup = useCallback(() => {
        setPopupElement(null);
      }, []);

      return (
        <WrappedComponent
          {...(props as P)}
          popupElement={popupElement}
          showPopup={showPopup}
          hidePopup={hidePopup}
          unMountPopup={unMountPopup}
        />
      );
    }

    return React.memo(ComponentWithPopup);
  };
}

export const withPopup = withPopupHigher('');
